import { useMemo, useState } from 'react';

export interface SparklinePoint {
  ym: string;   // "2024-03"
  n: number;
}

interface Props {
  series?: SparklinePoint[] | null;
  firstSeen?: string | null;
  lastSeen?: string | null;
  height?: number;
  color?: string;            // accent for bars
}

function daysAgo(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const days = Math.max(0, Math.round((Date.now() - t) / 86_400_000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 60) return `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 24) return `${months} months ago`;
  return `${Math.round(days / 365)} years ago`;
}

function formatYM(ym: string): string {
  const [y, m] = ym.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = parseInt(m, 10) - 1;
  return idx >= 0 && idx < 12 ? `${months[idx]} ${y}` : ym;
}

export default function MentionsSparkline({ series, firstSeen, lastSeen, height = 36, color }: Props) {
  const data = useMemo(() => series || [], [series]);
  const max = data.reduce((m, p) => Math.max(m, p.n), 0);
  const hasBars = data.length > 0 && max > 0;
  const [hover, setHover] = useState<number | null>(null);

  const bg = color || 'var(--interactive-accent, #58a8e8)';

  return (
    <div className="entity-sparkline">
      {hasBars ? (
        <div className="entity-sparkline-bars" style={{ height }}>
          {data.map((p, i) => {
            const ratio = p.n / max;
            const h = p.n === 0 ? 2 : Math.max(3, Math.round(ratio * height));
            const isHover = hover === i;
            const opacity = p.n === 0 ? 0.18 : 0.55 + 0.45 * ratio;
            return (
              <div
                key={p.ym}
                className={`entity-sparkline-bar ${isHover ? 'is-hover' : ''}`}
                style={{ height: h, background: bg, opacity: isHover ? 1 : opacity }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((cur) => (cur === i ? null : cur))}
              >
                {isHover && (
                  <div className="entity-sparkline-tooltip" style={{ borderColor: bg }}>
                    <span className="entity-sparkline-tooltip-count" style={{ color: bg }}>{p.n}</span>
                    <span className="entity-sparkline-tooltip-label">{formatYM(p.ym)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="entity-sparkline-empty" style={{ height }} />
      )}
      <div className="entity-sparkline-axis">
        <span>{firstSeen || '—'}</span>
        <span className="entity-sparkline-spacer" />
        <span>{lastSeen || '—'}{lastSeen && ` · ${daysAgo(lastSeen)}`}</span>
      </div>
    </div>
  );
}
